import { useTranslation } from 'react-i18next';
import { useContext, useEffect, useMemo, useState } from 'react';
import { ThemeContext } from '@f_context/Theme_context';
import { VirtualTable } from '@f_general/stackTable/VirtualTable';
import { ColumnDef } from '@tanstack/react-table';
import { useWindowSize } from '@f_hooks/index';
import { FilterContext } from '@f_qwep/context/Filter_context';
import { BasketContext } from '@f_qwep/context/Basket_context';
import stls from '../../../styles/component/basket/MiniBasket.module.sass';
import { CellQuantityBasket } from './CellQuantityBasket';
import { CellCommentBasket, CellDeleteBasket } from './CellCommentBasket';
import { CellMobile } from '../BasketTable/CellMobile';
import { QwepContext } from '@f_qwep/context/Qwep_context';
import { inRange } from 'lodash';
import { CellPrice } from '../BasketTable/CellPrice';
import { CellManufacturer } from '../BasketTable/CellManufacturer';
import { CellTotal } from './CellDateGeneralBasket';

export function MiniBasket({
    basket,
    accountId,
    basketId,
    basketForm,
    checkedItems,
    setCheckedItems,
    hasPartOrder,
}: any) {
    const { t } = useTranslation();
    const { colors, currentTheme } = useContext(ThemeContext);
    const [mobile, setMobile] = useState(false);
    const { width } = useWindowSize();

    const { searchWord } = useContext(BasketContext);
    const { filterBasket } = useContext(FilterContext);
    const { priceMarginIsActive, priceMarginState } = useContext(QwepContext);

    const [basketTable, setBasketTable] = useState(basket);

    useEffect(() => {
        setBasketTable(basket);
    }, [basket]);

    useEffect(() => {
        setMobile(width < 700);
    }, [width]);

    useEffect(() => {
        setBasketTable(
            basket.filter((item: any) => {
                if (
                    item?.brand?.toLocaleLowerCase().includes(searchWord) ||
                    item?.article?.toLocaleLowerCase().includes(searchWord) ||
                    item?.warehouse?.toLocaleLowerCase().includes(searchWord) ||
                    item?.title?.toLocaleLowerCase().includes(searchWord)
                ) {
                    return item;
                }
            }),
        );
    }, [searchWord]);

    // Фильтруем результаты
    const getFiltredResults = () => {
        let result: any[] = [];

        if (basketTable) {
            result = basketTable;
        }

        // БРЕНДЫ
        if (filterBasket?.brands.length > 0) {
            result = result.filter((row) => filterBasket?.brands.includes(row.brand));
        }
        if (priceMarginIsActive) {
            const rules = priceMarginState?.length && priceMarginState?.filter((el: any) => el.flag)[0]?.content;

            const getPriceWithMargin = (price: any) => {
                const currentPrice = Math.ceil(parseFloat(price));
                const currentRules = rules?.filter((rule: any) => inRange(currentPrice, rule?.from, rule.to));
                if (currentRules?.length > 0) {
                    const rule = currentRules?.[0];
                    return currentPrice + (currentPrice / 100) * rule.addPercent;
                }
                return price;
            };

            result = result.map((row) => ({
                ...row,
                priceWithMargin: getPriceWithMargin(row.price),
            }));
        }

        return result;
    };

    const data = useMemo(() => getFiltredResults() ?? [], [basketTable, filterBasket]);

    const columns = useMemo<ColumnDef<any>[]>(
        () =>
            mobile
                ? [
                      {
                          id: 'vendor',
                          accessorKey: 'Поставщик',
                          cell: (info) => (
                              <CellMobile
                                  info={info}
                                  basketForm={basketForm}
                                  accountId={accountId}
                                  basketId={basketId}
                                  hasPartOrder={hasPartOrder}
                                  checkedItems={checkedItems}
                                  setCheckedItems={setCheckedItems}
                              />
                          ),
                          footer: (props) => props.column.id,
                      },
                  ]
                : [
                      // {
                      //   id: 'date',
                      //   accessorKey: '',
                      //   cell: (info) => <CellDateBasket info={info} accountId={accountId} basketId={basketId} checkedItems={checkedItems} setCheckedItems={setCheckedItems} hasPartOrder={hasPartOrder}/>,
                      //   footer: (props) => props.column.id,
                      //   size: 1,
                      // },
                      {
                          id: 'Производитель',
                          accessorKey: 'manufacturer',
                          cell: (info) => (
                              <CellManufacturer
                                  info={info}
                                  accountId={accountId}
                                  basketId={basketId}
                                  hasPartOrder={hasPartOrder}
                                  basketForm={basketForm}
                              />
                          ),
                          footer: (props) => props.column.id,
                          size: 100,
                      },
                      {
                          id: 'Артикул',
                          accessorKey: 'pin',
                          cell: (info) => (
                              <p style={{ color: colors.alpha }} className={stls.cellArticle}>
                                  {info.row.original.article}
                              </p>
                          ),
                          footer: (props) => props.column.id,
                          size: 100,
                      },
                      {
                          id: 'Наименование',
                          accessorKey: 'description',
                          cell: (info) => <p className={stls.cellTitle}>{info.row.original.title}</p>,
                          footer: (props) => props.column.id,
                          size: 180,
                      },
                      {
                          id: 'Количество',
                          accessorKey: 'quantity',
                          cell: (info) => <CellQuantityBasket info={info} accountId={accountId} basketId={basketId} />,
                          footer: (props) => props.column.id,
                          size: 100,
                      },
                      {
                          id: 'Цена',
                          accessorKey: 'price',
                          cell: (info) => <CellPrice info={info} />,
                          footer: (props) => props.column.id,
                          aggregationFn: 'sum',
                          size: 80,
                      },
                      {
                          id: 'Склад',
                          accessorKey: 'warehouse',
                          cell: (info) => (
                              <div className={stls.cellDiv}>
                                  <p className={stls.cellWarehouse}> {info.row.original.warehouse}</p>
                              </div>
                          ),
                          footer: (props) => props.column.id,
                          size: 150,
                      },
                      {
                          id: 'comment',
                          accessorKey: 'notes',
                          cell: (info) => <CellCommentBasket info={info} />,
                          footer: (props) => props.column.id,
                          size: 1,
                      },
                      {
                          id: 'Итого',
                          accessorKey: '',
                          cell: (info) => <CellTotal info={info} />,
                          footer: (props) => props.column.id,
                          aggregationFn: 'mean',
                          size: 80,
                      },
                      // {
                      //   id: 'Оформить',
                      //   accessorKey: '',
                      //   cell: (info) => <CellCheckoutBasket info={info} basketForm={basketForm} accountId={accountId} basketId={basketId} hasPartOrder={hasPartOrder}/>,
                      //   footer: (props) => props.column.id,
                      //   aggregationFn: 'mean',
                      //   size: 85,
                      // },
                      {
                          id: 'Удалить',
                          accessorKey: '',
                          cell: (info) => <CellDeleteBasket info={info} accountId={accountId} basketId={basketId} />,
                          footer: (props) => props.column.id,
                          aggregationFn: 'mean',
                          size: 1,
                      },
                  ],

        [mobile, basketForm, basketTable, checkedItems],
    );

    const tableStyles = {
        head: {
            miniFilterInput: true,
            container: {
                height: '40px',
            },
            base: false,
            title: {
                fontSize: '11px',
            },
        },
        body: {
            thead: {
                visibility: 'collapse',
            },
            rowRadius: true,
            row: {
                background: colors.psi,
                height: '20px',
            },
            marginRow: {
                margin: '1px 0',
            },
            cellRadius: false,
        },
    };

    return (
        <>
            {basket?.length ? (
                <VirtualTable
                    data={data}
                    columnsTable={columns}
                    topFilterPanel={false}
                    showColumnSearchAction={false}
                    configHeader={{
                        fullScrean: false,
                        exportExcel: false,
                        resize: false,
                        grouping: false,
                        columnFilter: false,
                        sorting: false,
                        columnSearch: false,
                        dragging: false,
                    }}
                    styles={tableStyles}
                />
            ) : (
                <></>
            )}
        </>
    );
}
